import {
  Currency,
  MultipleType,
  Denomination,
  ProjectStatus,
  CalcMethod,
  OpmSimulatedValue,
  CollaringMode,
  OpmInputDataSource,
  ProjectCompatibilityLevel,
} from '@app/shared/models/contracts/enums/shared-enums';
import { DetailsDto, PerYearValuesDto, ProjectDto } from '@app/shared/models/contracts/project-dto';
import { cloneToMutableData, enumKeyByValue } from '@app/shared/helpers';
import { DEFAULT_EMPTY_BENCHMARKING } from './empty-benchmarking';
import { l } from '@locale/setupI18n';
import { v4 as uuidv4 } from 'uuid';

export interface ProjectFormModel extends DetailsDto {
  projectName: string;
  status: keyof typeof ProjectStatus;
  valuationDate: Nullable<string>;
  investmentDate: Nullable<string>;
  yearEndDate: Nullable<string>;
  storeBenchmarkingData: boolean;
  randomiserActive: boolean;
  iterationCount: number;
  opmSimulatedValue: keyof typeof OpmSimulatedValue;
}

const getProjectFormBase = (model: ProjectFormModel) => ({
  name: model.projectName,
  storeBenchmarkingData: model.storeBenchmarkingData,
  valuationDate: model.valuationDate ?? '',
  yearEndDate: model.yearEndDate ?? '',
  investmentDate: model.investmentDate ?? '',
});

const getValidYearEndDates = (
  yearEndDate?: string | null,
  investmentDate?: string | null,
  valuationDate?: string | null
): string[] => {
  if (!yearEndDate || !investmentDate || !valuationDate) {
    return [];
  }
  const [, yearEndMonth, yearEndDay] = yearEndDate.split('-');
  const startYear = Number(investmentDate.split('-')[0]);
  const endYear = Number(valuationDate.split('-')[0]);
  return Array.from({ length: endYear - startYear + 1 }, (_, i) => {
    const year = startYear + i;
    const yearEnd = `${year}-${yearEndMonth}-${yearEndDay}`;
    const yearEndDateObj = new Date(yearEnd);
    const investmentDateObj = new Date(investmentDate);
    const valuationDateObj = new Date(valuationDate);
    return yearEndDateObj >= investmentDateObj && yearEndDateObj <= valuationDateObj
      ? yearEnd
      : null;
  }).filter(Boolean) as string[];
};

export function MapNewProjectAdminToProjectDto(
  model: ProjectFormModel,
  latestCompatibilityLevel: ProjectCompatibilityLevel
): ProjectDto {
  const valueDriverMetricId = uuidv4();

  // @ts-ignore
  return {
    ...getProjectFormBase(model),
    status: enumKeyByValue(ProjectStatus, ProjectStatus.Draft),
    compatibilityLevel: latestCompatibilityLevel,
    id: 0,
    benchmarking: { ...DEFAULT_EMPTY_BENCHMARKING },
    capitalStructures: {},
    sourcesAndUses: {
      equitySources: [],
      otherSources: [],
      uses: [],
      updatedSpotEquity: 0,
    },
    valuationInputs: {
      minorityDiscounts: {
        dloc: null,
        controlPremiumForDloc: {},
        marketVolatility: null,
        peerSetVolatility: null,
      },
    },
    details: {
      currency: enumKeyByValue(Currency, Currency.PoundSterling),
      denomination: enumKeyByValue(Denomination, Denomination.Thousands),
      calcMethod: enumKeyByValue(CalcMethod, CalcMethod.PWERM),
      mipEvent: null,
      dealThesisExitMultipleType: enumKeyByValue(MultipleType, MultipleType.Ltm),
      sector: null,
      subSector: null,
      regionOfOperations: null,
      countryOfMainOperations: null,
      mainTaxJurisdiction: null,
      secondaryTaxJurisdiction: null,
      client: null,
      targetTradeName: null,
      sponsor: null,
      coInvestor: null,
      engagementLetterDate: null,
      valuationProjectLead: null,
      dealsTaxProjectLead: null,
      salesforceId: null,
      qcSignOff: false,
      qvpSignOff: false,
      qcSignOffUsername: null,
      qvpSignOffUsername: null,
    },
    opmInput: {
      evValue: 0,
      evValueSource: OpmInputDataSource.FromCapitalStructure,
      forecastYears: 3,
      forecastYearsSource: OpmInputDataSource.FromPWERM,
      evVolatility: 0,
      randomSeed: Math.floor(Math.random() * 10000),
      iterationCount: null,
      randomiserActive: false,
      selectedEventSetId: null,
      selectedCaseId: null,
      opmSimulatedValue: enumKeyByValue(OpmSimulatedValue, OpmSimulatedValue.EnterpriseValue),
      netDebtItems: [],
      initialCashValue: 0,
      realWorldRate: null,
      controlPremiumForDloc: 0,
      controlPremiumForDlocByInstrument: {},
      controlPremiumForDlocByInstrumentSource: {},
      controlPremiumForDlocSource: OpmInputDataSource.FromPWERM,
      ValuationConclusion: [],
      fixedValueCap: null,
      percentBottom: null,
      percentTop: null,
      perYearInputs: [
        createOpmPerYearInput(model.yearEndDate!, 0),
        createOpmPerYearInput(new Date(model.yearEndDate!).addYears(1).toISODateString(), 0),
        createOpmPerYearInput(new Date(model.yearEndDate!).addYears(2).toISODateString(), 100),
      ],
      valuationDateNarrative: '',
      initialTotalNetDebt: 0,
      initialTotalNetDebtSource: OpmInputDataSource.FromPWERM,
      marketVolatility: null,
      peerSetVolatility: null,
      exitScenarios: {},
      netDebtPerEvent: {},
      selectedDividendSetId: uuidv4(),
    },
    overallConclusions: [],
    pwermInput: {
      metricDefinitions: {
        [valueDriverMetricId]: { narrative: l('_DefaultExitValueDriverNarrative') },
      },
      valueDriverActiveMetric: valueDriverMetricId,
      valuationDateMetrics: {
        [valueDriverMetricId]: 0,
      },
      valuationDateNarrative: l('_DefaultValuationDateNarrative'),
      capmAssumptions: {
        riskFreeRate: 0,
        assetBeta: 0,
        emrp: 0,
        cemra: 0,
        riskFreeRateCountry: null,
        sourceAndRunDate: null,
      },
      cases: [],
      historicForecasts: [],
      discountRateOverrideActive: false,
    },
    equityBridge: { columns: [], netDebtItems: [], commonNetDebtItemIds: [] },
  };
}

export function MapUpdatedProjectAdminToProjectDto(
  model: ProjectFormModel,
  currentDraftState: ProjectDto
): ProjectDto {
  const capitalStructureId = Object.keys(currentDraftState.capitalStructures)[0];
  let historicForecasts = currentDraftState.pwermInput.historicForecasts;
  const forecastDividendSets = cloneToMutableData(
    currentDraftState.capitalStructures[capitalStructureId].forecastDividendSets
  );
  let historicDividendInstruments = cloneToMutableData(
    currentDraftState.capitalStructures[capitalStructureId].historicDividendInstruments
  );

  const yearEndDate = model.yearEndDate ?? currentDraftState.yearEndDate;
  const investmentDate = model.investmentDate ?? currentDraftState.investmentDate;
  const valuationDate = model.valuationDate ?? currentDraftState.valuationDate;

  // If the yearEndDate, investmentDate, or valuationDate has changed, we need to update the existing historicDividendInstruments
  if (
    historicDividendInstruments &&
    ((model.investmentDate && currentDraftState.investmentDate !== model.investmentDate) ||
      (model.valuationDate && currentDraftState.valuationDate !== model.valuationDate))
  ) {
    if (yearEndDate && investmentDate && valuationDate) {
      const years = getValidYearEndDates(yearEndDate, investmentDate, valuationDate);

      historicDividendInstruments = Object.fromEntries(
        Object.entries(historicDividendInstruments).map(([instrumentId, instrument]) => {
          const updatedDates = Object.fromEntries(
            years.map((year) => [
              year,
              {
                totalDividendValue: instrument.dates[year]?.totalDividendValue ?? null,
              },
            ])
          );
          return [
            instrumentId,
            {
              ...instrument,
              dates: updatedDates,
            },
          ];
        })
      );
    }
  }

  // If there were no existing historicDividendInstruments, but we have an instrument eligible for dividends,
  // and the investmentDate or valuationDate has changed, we need to create a new historicDividendInstruments
  if (
    !historicDividendInstruments &&
    Object.values(
      currentDraftState.capitalStructures[capitalStructureId].instrumentDefinitions
    ).some((instrument) => instrument.dividendEligible) &&
    (model.investmentDate || currentDraftState.investmentDate) &&
    (model.valuationDate || currentDraftState.valuationDate)
  ) {
    if (yearEndDate && investmentDate && valuationDate) {
      const years = getValidYearEndDates(yearEndDate, investmentDate, valuationDate);

      historicDividendInstruments = Object.fromEntries(
        Object.entries(
          currentDraftState.capitalStructures[capitalStructureId].instrumentDefinitions
        )
          .filter(([_, instrument]) => instrument.dividendEligible)
          .map(([instrumentId, _]) => [
            instrumentId,
            {
              instrumentId,
              dates: Object.fromEntries(years.map((year) => [year, { totalDividendValue: null }])),
            },
          ])
      );
    }
  }

  if (model.investmentDate && currentDraftState.investmentDate !== model.investmentDate) {
    const investmentDate = new Date(model.investmentDate);
    historicForecasts = currentDraftState.pwermInput.historicForecasts.filter(
      (forecast) => new Date(forecast.date) > investmentDate
    );
  }

  const projectDtoBase: ProjectDto = {
    ...currentDraftState,
    ...getProjectFormBase(model),
    status: model.status,
    details: {
      ...currentDraftState.details,
      currency: model.currency,
      denomination: model.denomination,
      mipEvent: model.mipEvent,
      calcMethod: model.calcMethod,
      dealThesisExitMultipleType: model.dealThesisExitMultipleType,
      sector: model.sector,
      subSector: model.subSector,
      regionOfOperations: model.regionOfOperations,
      countryOfMainOperations: model.countryOfMainOperations,
      mainTaxJurisdiction: model.mainTaxJurisdiction,
      secondaryTaxJurisdiction: model.secondaryTaxJurisdiction,
      client: model.client ?? null,
      targetTradeName: model.targetTradeName ?? null,
      sponsor: model.sponsor ?? null,
      coInvestor: model.coInvestor ?? null,
      engagementLetterDate: model.engagementLetterDate ?? null,
      valuationProjectLead: model.valuationProjectLead ?? null,
      dealsTaxProjectLead: model.dealsTaxProjectLead ?? null,
      salesforceId: model.salesforceId === '' ? null : model.salesforceId,
      qcSignOff: model.qcSignOff ?? false,
      qvpSignOff: model.qvpSignOff ?? false,
      qcSignOffUsername: model.qcSignOffUsername ?? null,
      qvpSignOffUsername: model.qvpSignOffUsername ?? null,
    },
    opmInput: {
      ...currentDraftState.opmInput,
      iterationCount: model.iterationCount,
      randomiserActive: model.randomiserActive ?? false,
      opmSimulatedValue: model.opmSimulatedValue,
    },
    pwermInput: {
      ...currentDraftState.pwermInput,
      historicForecasts,
    },
    capitalStructures: {
      ...currentDraftState.capitalStructures,
      [capitalStructureId]: {
        ...currentDraftState.capitalStructures[capitalStructureId],
        historicDividendInstruments: historicDividendInstruments,
      },
    },
  };

  if (model.yearEndDate && currentDraftState.yearEndDate !== model.yearEndDate) {
    const [startYear, yearEndMonth, yearEndDay] = model.yearEndDate.split('-');

    const updatedForecastDividendSets = Object.fromEntries(
      Object.entries(forecastDividendSets).map(([setId, set]) => [
        setId,
        {
          ...set,
          instruments: Object.fromEntries(
            Object.entries(set.instruments).map(([instrumentId, instrument]) => {
              const oldDates = Object.keys(instrument.dates);
              const updatedDates = Object.fromEntries(
                oldDates.map((_, idx) => {
                  const newYear = Number(startYear) + idx;
                  const newYearString = String(newYear).padStart(4, '0');
                  const newDate = `${newYearString}-${yearEndMonth}-${yearEndDay}`;
                  return [
                    newDate,
                    {
                      totalDividendValue:
                        instrument.dates[oldDates[idx]]?.totalDividendValue ?? null,
                    },
                  ];
                })
              );
              return [
                instrumentId,
                {
                  ...instrument,
                  dates: updatedDates,
                },
              ];
            })
          ),
        },
      ])
    );

    let updatedHistoricDividendInstruments = cloneToMutableData(historicDividendInstruments);
    if (historicDividendInstruments && investmentDate && valuationDate) {
      const years = getValidYearEndDates(yearEndDate, investmentDate, valuationDate);

      updatedHistoricDividendInstruments = Object.fromEntries(
        Object.entries(historicDividendInstruments).map(([instrumentId, instrument]) => {
          const oldValues = Object.values(instrument.dates).map((d) => d.totalDividendValue);
          const padLength = years.length - oldValues.length;
          const paddedValues = Array(Math.max(0, padLength)).fill(null).concat(oldValues);
          const updatedDates = Object.fromEntries(
            years.map((yearEndDateStr, idx) => [
              yearEndDateStr,
              { totalDividendValue: paddedValues[idx] },
            ])
          );
          return [
            instrumentId,
            {
              ...instrument,
              dates: updatedDates,
            },
          ];
        })
      );
    }

    return {
      ...projectDtoBase,
      pwermInput: {
        ...currentDraftState.pwermInput,
        cases: currentDraftState.pwermInput.cases.map((_case) => {
          return {
            ..._case,
            forecasts: [..._case.forecasts]
              .sort((a, b) => a.forecastYear.localeCompare(b.forecastYear))
              .map((forecast, index) => {
                return {
                  ...forecast,
                  forecastYear: new Date(model.yearEndDate!).addYears(index).toISODateString(),
                };
              }),
          };
        }),
      },
      opmInput: {
        ...currentDraftState.opmInput,
        perYearInputs: [...currentDraftState.opmInput.perYearInputs]
          .sort((a, b) => a.forecastDate.localeCompare(b.forecastDate))
          .map((perYearInput, index) => {
            return {
              ...perYearInput,
              forecastDate: new Date(model.yearEndDate!).addYears(index).toISODateString(),
            };
          }),
      },
      capitalStructures: {
        ...currentDraftState.capitalStructures,
        [capitalStructureId]: {
          ...currentDraftState.capitalStructures[capitalStructureId],
          forecastDividendSets: updatedForecastDividendSets,
          historicDividendInstruments: updatedHistoricDividendInstruments,
        },
      },
    };
  }

  return projectDtoBase;
}

function createOpmPerYearInput(
  yearEndDate: string,
  exitHorizonProbability: number
): PerYearValuesDto {
  return {
    exitHorizonProbability: exitHorizonProbability,
    riskFreeRate: 0,
    forecastDate: yearEndDate,
    equitySoldInIpo: 0,
    equitySoldInIpoSource: OpmInputDataSource.FromPWERM,
    financingCosts: 0,
    financingCostsSource: OpmInputDataSource.FromPWERM,
    hypotheticalDebtWhenIpo: 0,
    hypotheticalDebtWhenIpoSource: OpmInputDataSource.FromPWERM,
    ipoCostsOfEv: 0,
    ipoCostsOfEvSource: OpmInputDataSource.FromPWERM,
    ipoDiscount: 0,
    ipoDiscountSource: OpmInputDataSource.FromPWERM,
    ipoProbability: 0,
    ipoProbabilitySource: OpmInputDataSource.FromPWERM,
    postIpoSaleDlom: 0,
    postIpoSaleDlomSource: OpmInputDataSource.FromPWERM,
    secondarySaleCostsOfEv: 0,
    secondarySaleCostsOfEvSource: OpmInputDataSource.FromPWERM,
    netDebtValues: [],
    marketValueDloms: [],
    cashValue: 0,
    totalNetDebt: 0,
    totalNetDebtSource: OpmInputDataSource.FromPWERM,
    collaring: { inputMode: CollaringMode.Off, percentage: null, fixedValue: null },
    operationalFreeCashFlow: 0,
    operationalFreeCashFlowSource: OpmInputDataSource.FromPWERM,
    operationalEv: 0,
    operationalEvSource: OpmInputDataSource.FromPWERM,
  };
}
